/**
 * Three small, synchronous primitives the wallet-address validation needs and
 * the platform does not otherwise have: SHA-256, Keccak-256 and Base58.
 *
 * WHY THEY ARE HERE AND NOT `crypto.subtle`. The address a creator types is
 * checked as they type it, in the browser, and again in the Worker before it
 * is sealed. WebCrypto has SHA-256 but it is asynchronous, and it has no
 * Keccak at all — EIP-55 (the mixed-case checksum on an Ethereum address) is
 * Keccak-256 and nothing else will do. A dependency would be a second source
 * of truth for a hash, shipped twice; these are a few hundred lines that run
 * once per keystroke on a forty-character string.
 *
 * NOT FOR ANYTHING ELSE. Session tokens, document hashes and payout
 * fingerprints stay on `crypto.subtle` in the API (`lib/digest.ts`); this
 * file exists for checksums on public addresses, which are not secrets.
 *
 * Pinned by `apps/api/test/crypto-address.test.ts` against the published
 * vectors: `sha256("abc")`, `keccak256("")`, and the EIP-55 examples.
 */

/* ------------------------------------------------------------------ */
/* SHA-256                                                              */
/* ------------------------------------------------------------------ */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/** SHA-256 of raw bytes, as raw bytes. Synchronous. */
export function sha256Bytes(message: Uint8Array): Uint8Array {
  const length = message.length;
  const bitLength = length * 8;
  /* Pad: 0x80, zeros to 56 mod 64, then the 64-bit big-endian bit length. */
  const padded = new Uint8Array(Math.ceil((length + 9) / 64) * 64);
  padded.set(message);
  padded[length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x1_0000_0000), false);
  view.setUint32(padded.length - 4, bitLength >>> 0, false);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i += 1) {
      const w15 = w[i - 15]!;
      const w2 = w[i - 2]!;
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }

    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;

    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }

  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  for (let i = 0; i < 8; i += 1) outView.setUint32(i * 4, h[i]!, false);
  return out;
}

/* ------------------------------------------------------------------ */
/* Keccak-256                                                           */
/* ------------------------------------------------------------------ */

/*
 * The ORIGINAL Keccak padding (0x01 … 0x80), which is what Ethereum uses and
 * what EIP-55 specifies — NOT the SHA-3 standard's 0x06. The two differ only
 * in that byte and produce entirely different digests; `keccak256("")` below
 * is the vector that tells them apart.
 */

const RC: readonly bigint[] = [
  0x0000000000000001n, 0x0000000000008082n, 0x800000000000808an, 0x8000000080008000n,
  0x000000000000808bn, 0x0000000080000001n, 0x8000000080008081n, 0x8000000000008009n,
  0x000000000000008an, 0x0000000000000088n, 0x0000000080008009n, 0x000000008000000an,
  0x000000008000808bn, 0x800000000000008bn, 0x8000000000008089n, 0x8000000000008003n,
  0x8000000000008002n, 0x8000000000000080n, 0x000000000000800an, 0x800000008000000an,
  0x8000000080008081n, 0x8000000000008080n, 0x0000000080000001n, 0x8000000080008008n,
];

/* Rotation offsets, indexed [x][y]. */
const ROT: readonly (readonly number[])[] = [
  [0, 36, 3, 41, 18],
  [1, 44, 10, 45, 2],
  [62, 6, 43, 15, 61],
  [28, 55, 25, 21, 56],
  [27, 20, 39, 8, 14],
];

const MASK64 = (1n << 64n) - 1n;
const rotl64 = (v: bigint, n: number): bigint =>
  n === 0 ? v : ((v << BigInt(n)) | (v >> BigInt(64 - n))) & MASK64;

function keccakF(state: bigint[]): void {
  for (let round = 0; round < 24; round += 1) {
    /* theta */
    const c: bigint[] = [];
    for (let x = 0; x < 5; x += 1) {
      c[x] = state[x]! ^ state[x + 5]! ^ state[x + 10]! ^ state[x + 15]! ^ state[x + 20]!;
    }
    for (let x = 0; x < 5; x += 1) {
      const d = c[(x + 4) % 5]! ^ rotl64(c[(x + 1) % 5]!, 1);
      for (let y = 0; y < 5; y += 1) state[x + 5 * y] = state[x + 5 * y]! ^ d;
    }

    /* rho and pi */
    const b: bigint[] = new Array<bigint>(25).fill(0n);
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        b[y + 5 * ((2 * x + 3 * y) % 5)] = rotl64(state[x + 5 * y]!, ROT[x]![y]!);
      }
    }

    /* chi */
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        state[x + 5 * y] =
          b[x + 5 * y]! ^ (~b[((x + 1) % 5) + 5 * y]! & MASK64 & b[((x + 2) % 5) + 5 * y]!);
      }
    }

    /* iota */
    state[0] = state[0]! ^ RC[round]!;
  }
}

/** Keccak-256 (Ethereum's flavour) of raw bytes, as raw bytes. Synchronous. */
export function keccak256Bytes(message: Uint8Array): Uint8Array {
  const RATE = 136;
  const state: bigint[] = new Array<bigint>(25).fill(0n);

  /* Pad to a whole number of rate blocks: 0x01 after the message, 0x80 on the
     last byte of the block (both on one byte when the message ends one short). */
  const blocks = Math.floor(message.length / RATE) + 1;
  const padded = new Uint8Array(blocks * RATE);
  padded.set(message);
  padded[message.length] = padded[message.length]! | 0x01;
  padded[padded.length - 1] = padded[padded.length - 1]! | 0x80;

  for (let offset = 0; offset < padded.length; offset += RATE) {
    for (let lane = 0; lane < RATE / 8; lane += 1) {
      let v = 0n;
      /* Little-endian lanes. */
      for (let byte = 7; byte >= 0; byte -= 1) {
        v = (v << 8n) | BigInt(padded[offset + lane * 8 + byte]!);
      }
      state[lane] = state[lane]! ^ v;
    }
    keccakF(state);
  }

  const out = new Uint8Array(32);
  for (let lane = 0; lane < 4; lane += 1) {
    let v = state[lane]!;
    for (let byte = 0; byte < 8; byte += 1) {
      out[lane * 8 + byte] = Number(v & 0xffn);
      v >>= 8n;
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Encoding helpers                                                     */
/* ------------------------------------------------------------------ */

/**
 * UTF-8 by hand: this package compiles without the DOM lib, so `TextEncoder`
 * is not a name it can use, and the only strings hashed here are addresses
 * and their lower-cased hex bodies — ASCII — plus the published test vectors.
 */
export function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        i += 1;
      }
    }
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(out);
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (clean.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(clean)) {
    throw new Error('not a hex string');
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export const sha256Hex = (text: string): string => bytesToHex(sha256Bytes(utf8Bytes(text)));
export const keccak256Hex = (text: string): string => bytesToHex(keccak256Bytes(utf8Bytes(text)));

/* ------------------------------------------------------------------ */
/* Base58 (Bitcoin alphabet — what TRON and Solana use)                 */
/* ------------------------------------------------------------------ */

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE58_INDEX = new Map<string, number>(
  [...BASE58_ALPHABET].map((char, index) => [char, index] as const),
);

/**
 * Decode Base58 into bytes, or null when a character is not in the alphabet.
 *
 * `0`, `O`, `I` and `l` are not Base58 characters — that is the alphabet's
 * whole reason for existing — so an address containing one is refused here
 * before any checksum is looked at, and the sentence the creator reads can
 * say so.
 */
export function base58Decode(text: string): Uint8Array | null {
  if (text.length === 0) return new Uint8Array(0);
  /* Little-endian digits of the numeric value, with no leading zero: the loop
     only ever pushes on a non-zero carry, so `'1111'` stays empty here and
     comes out below as four zero bytes rather than five. */
  const bytes: number[] = [];
  for (const char of text) {
    let carry = BASE58_INDEX.get(char);
    if (carry === undefined) return null;
    for (let i = 0; i < bytes.length; i += 1) {
      carry += bytes[i]! * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  /* Each leading '1' is one leading zero byte. */
  let leading = 0;
  for (const char of text) {
    if (char !== '1') break;
    leading += 1;
  }
  const out = new Uint8Array(leading + bytes.length);
  for (let i = 0; i < bytes.length; i += 1) out[leading + i] = bytes[bytes.length - 1 - i]!;
  return out;
}

export function base58Encode(bytes: Uint8Array): string {
  const digits: number[] = [];
  for (const byte of bytes) {
    let carry = byte;
    for (let i = 0; i < digits.length; i += 1) {
      carry += digits[i]! << 8;
      digits[i] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let out = '';
  for (const byte of bytes) {
    if (byte !== 0) break;
    out += '1';
  }
  for (let i = digits.length - 1; i >= 0; i -= 1) out += BASE58_ALPHABET[digits[i]!];
  return out;
}
